import {
  isSchoolEventDismissed,
  isSchoolEventDuplicate,
  resolveSchoolEvent,
} from "./resolveSchoolEvent.js";

const ATTENTION_TYPES = new Set(["task", "event", "prep", "bring"]);

/** Today as YYYY-MM-DD in Europe/Zurich (matches extraction timezone). */
export function todayYmdZurich(now = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Zurich",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

export function isAttentionActionType(actionType) {
  return ATTENTION_TYPES.has(String(actionType || "").toLowerCase());
}

export function isTaskActionType(actionType) {
  const t = String(actionType || "").toLowerCase();
  return t === "task" || t === "prep" || t === "bring";
}

export function isEventActionType(actionType) {
  return String(actionType || "").toLowerCase() === "event";
}

export function isUpcomingDated(event, todayYmd) {
  const date = String(event?.date || "");
  return Boolean(date) && date >= todayYmd;
}

function compareByDateTime(a, b) {
  const dateA = String(a?.date || "");
  const dateB = String(b?.date || "");
  if (dateA !== dateB) return dateA.localeCompare(dateB);
  return String(a?.time || "").localeCompare(String(b?.time || ""));
}

/**
 * Resolve, filter dismissals, keep upcoming attention types, sort by date/time.
 * @param {object[]|null|undefined} events
 * @param {object|null|undefined} corrections
 * @param {string} todayYmd
 * @param {{ includeDismissed?: boolean }} [opts]
 */
export function mergeAndSortAttentionItems(
  events,
  corrections,
  todayYmd = todayYmdZurich(),
  { includeDismissed = false } = {}
) {
  const items = [];

  (events || []).forEach((raw, index) => {
    if (!includeDismissed && isSchoolEventDismissed(corrections, index)) return;
    if (isSchoolEventDuplicate(raw)) return;
    const resolved = resolveSchoolEvent(raw, corrections, index);
    if (!resolved) return;
    if (isSchoolEventDuplicate(resolved)) return;
    if (!isAttentionActionType(resolved.action_type)) return;
    if (!isUpcomingDated(resolved, todayYmd)) return;
    items.push({ ...resolved, index });
  });

  items.sort(compareByDateTime);
  return items;
}

/**
 * Split one mail's events for parent UI: upcoming attention vs quiet context.
 * @param {object[]|null|undefined} events
 * @param {object|null|undefined} corrections
 * @param {string} [todayYmd]
 * @returns {{ upcomingAttention: object[], info: object[], pastOrUndatedInfo: object[] }}
 */
export function partitionMailEvents(
  events,
  corrections,
  todayYmd = todayYmdZurich()
) {
  const upcomingAttention = [];
  const info = [];

  (events || []).forEach((raw, index) => {
    if (isSchoolEventDuplicate(raw)) return;
    const resolved = resolveSchoolEvent(raw, corrections, index);
    if (!resolved) return;
    if (isSchoolEventDuplicate(resolved)) return;
    const withIndex = { ...resolved, index };
    if (
      isAttentionActionType(withIndex.action_type) &&
      isUpcomingDated(withIndex, todayYmd)
    ) {
      upcomingAttention.push(withIndex);
    } else {
      info.push(withIndex);
    }
  });

  upcomingAttention.sort(compareByDateTime);
  const pastOrUndatedInfo = info.filter(
    (event) =>
      !isAttentionActionType(event.action_type) ||
      !isUpcomingDated(event, todayYmd)
  );

  return { upcomingAttention, info, pastOrUndatedInfo };
}

/** @deprecated Use partitionMailEvents — kept for callers expecting { upcoming, info }. */
export function partitionSchoolEmailForDisplay(events, today = todayYmdZurich()) {
  const upcomingAttention = [];
  const info = [];

  (events || []).forEach((event, index) => {
    const withIndex = { ...event, index: event.index ?? index };
    if (
      isAttentionActionType(withIndex.action_type) &&
      isUpcomingDated(withIndex, today)
    ) {
      upcomingAttention.push(withIndex);
    } else {
      info.push(withIndex);
    }
  });

  upcomingAttention.sort(compareByDateTime);
  return { upcoming: upcomingAttention, info };
}
